'use client';

import { useEffect, useState } from 'react';
import { useRegisterServiceWorker } from '@/hooks';
import Script from 'next/script';
import Salesforce, { SalesforceProps } from './Salesforce';
import SalesforcePzn, { SalesforcePznProps } from './SalesforcePzn';
import { deferIfPrerendering } from '@/utils/deferIfPrerendering';
import { useDatadogRum } from '@/hooks/useDatadogRum';
import { useSessionParamSync } from '@/hooks/useSessionParamSync';

interface ClientPageHeadProps {
    envName: string;
    ddenv: string;
    launchScript: string;
    onetrust: {
        script: string;
        domainScript: string;
    };
    pageInfo: {
        analyticsPageName: string;
        analyticsPageType: string;
        pageType: PageType;
    };
    abTest?: SalesforceProps;
    pznAbTest?: SalesforcePznProps;
}

export default function ClientPageHead(props: ClientPageHeadProps): React.JSX.Element {
    const [isAnalyticsReady, setIsAnalyticsReady] = useState<boolean>(false);
    const [ddLoaded, setDdLoaded] = useState<boolean>(false);

    useRegisterServiceWorker('/service-worker.js');

    useEffect(() => {
        window.dispatchEvent(new Event('hydrate'));
    }, []);

    useSessionParamSync();
    useDatadogRum({
        loaded: ddLoaded,
        ddenv: props.ddenv,
        pageInfo: props.pageInfo,
    });

    return (
        <>
            <Script
                src="https://www.datadoghq-browser-agent.com/us1/v5/datadog-rum.js"
                onLoad={() => {
                    setDdLoaded(true);
                }}
            />

            <Script
                src={props.launchScript}
                async
                strategy="afterInteractive"
                onLoad={() => {
                    const script = document.createElement('script');
                    script.setAttribute('src', '/resources/js/analytics.js');
                    script.onload = () => setIsAnalyticsReady(true);
                    document.body.appendChild(script);
                }}
            />

            {process.env.NODE_ENV !== 'development' && (
                <Script
                    src={props.onetrust.script}
                    strategy="afterInteractive"
                    data-domain-script={props.onetrust.domainScript}
                    onLoad={() => {
                        const browserWindow = window as BrowserWindow;
                        browserWindow.OptanonWrapper = () => {
                            const activeGroups = browserWindow.OnetrustActiveGroups ?? '';

                            browserWindow.DD_RUM?.setGlobalContextProperty('consent', activeGroups);
                            window.dispatchEvent(
                                new CustomEvent('oneTrustConsentChanged', {
                                    detail: { activeGroups },
                                }),
                            );
                            if (!activeGroups.includes('C0004')) {
                                const clearPersonalizationCookies = () => {
                                    const expired =
                                        'expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.siriusxm.com;';
                                    const personalizationCookies = [
                                        'sxm_offer_seg',
                                        'sxm_user_info',
                                        'sxm_content_seg',
                                    ];
                                    personalizationCookies.forEach(
                                        (cookie) => (document.cookie = `${cookie}=; ${expired}`),
                                    );
                                };

                                // defer cookie deletion during prerender - these cookies are scoped to
                                // .siriusxm.com so deleting them early would affect care.siriusxm.com
                                // and other subdomains before the user has actually landed on the page
                                deferIfPrerendering(clearPersonalizationCookies);
                            }
                        };
                    }}
                />
            )}
            {isAnalyticsReady && props.abTest && <Salesforce {...props.abTest} />}
            {isAnalyticsReady && props.pznAbTest && <SalesforcePzn {...props.pznAbTest} />}
        </>
    );
}
